/**
 * Untamed v2.1 - Product Page JavaScript
 * ============================================
 * Handles variant selection, option availability, and media gallery interactions.
 */

(function() {
  'use strict';

  /**
   * Product Variant Selector Class
   * Handles variant selection, option availability updates, and UI synchronization
   */
  class ProductVariantSelector {
    constructor(config) {
      this.variants = config.variants || [];
      this.options = config.options || [];
      this.moneyFormat = config.moneyFormat || '${{amount}}';
      this.currentVariantId = config.currentVariantId;
      this.sectionId = config.sectionId || null;
      
      // Get container (scoped to section if sectionId provided)
      this.container = this.sectionId 
        ? document.querySelector(`[data-section-id="${this.sectionId}"]`)
        : document;
      
      if (!this.container) return;
      
      // Initialize selected options from current variant
      this.selectedOptions = {};
      const currentVariant = this.variants.find(v => v.id === this.currentVariantId);
      this.options.forEach((option, index) => {
        const optionKey = 'option' + (index + 1);
        this.selectedOptions[option.name] = currentVariant ? currentVariant[optionKey] : option.values[0];
      });
      
      this.init();
    }
    
    init() {
      this.syncUIState();
      this.updateOptionAvailability();
      this.bindEvents();
      this.bindExternalVariantChanges();
    }
    
    syncUIState() {
      // Sync button active states to match current variant
      Object.entries(this.selectedOptions).forEach(([optionName, optionValue]) => {
        const btn = this.container.querySelector(`[data-option-name="${optionName}"][data-option-value="${optionValue}"]`);
        if (btn) {
          const container = btn.closest('.product-variants-block__pills, .product-variants-block__swatches');
          if (container) {
            container.querySelectorAll('button').forEach(b => b.classList.remove('active'));
          }
          btn.classList.add('active');
          
          const label = btn.closest('.product-variants-block__option')?.querySelector('.product-variants-block__selected-value');
          if (label) label.textContent = ' ' + optionValue;
        }
      });
    }
    
    bindEvents() {
      // Pills and swatches
      this.container.querySelectorAll('.product-variants-block__pill, .product-variants-block__swatch').forEach(btn => {
        btn.addEventListener('click', (e) => this.handleOptionClick(e));
      });
      
      // Dropdowns
      this.container.querySelectorAll('.product-variants-block__dropdown').forEach(dropdown => {
        dropdown.addEventListener('select:change', (e) => this.handleDropdownChange(e, dropdown));
      });
    }
    
    // ===== Third-party swatch app bridge (e.g. Globo) =====
    // Swatch apps replace the theme's native option buttons with their own UI and
    // toggle hidden option inputs (radios/selects) instead. The theme therefore
    // never hears about the change. We watch those inputs, reconstruct the chosen
    // variant, and drive the theme (image switch, price, availability AND the
    // cart's [name="id"]) — the same work handleOptionClick does for native UI.
    bindExternalVariantChanges() {
      this._lastAppliedVariantId = this.currentVariantId;

      var self = this;
      var handler = function() { self.checkExternalVariantChange(); };

      // Fires when an app toggles a radio / changes a select.
      document.addEventListener('change', handler, true);
      // Safety net for apps that set values without emitting a change event, and
      // to catch the app's initial render shortly after load.
      this._externalPollId = setInterval(handler, 400);
      setTimeout(handler, 300);
      setTimeout(handler, 1000);
    }

    // Reconstruct the selected variant from whatever option controls are on the
    // page, without assuming how the app names them: a variant matches when every
    // one of its option values is currently selected among the radios/selects.
    getSelectedVariantFromDOM() {
      var scope = document.querySelector('form[action*="/cart/add"]') || document;

      var selectedValues = new Set();
      scope.querySelectorAll('input[type="radio"]:checked').forEach(function(r) {
        if (r.value) selectedValues.add(r.value);
      });
      scope.querySelectorAll('select').forEach(function(s) {
        if (s.value) selectedValues.add(s.value);
      });

      var match = this.variants.find((variant) => {
        return this.options.every((option, index) => selectedValues.has(variant['option' + (index + 1)]));
      });
      if (match) return match;

      // Fallback: a valid variant id already sitting in the cart input.
      var idInput = scope.querySelector('[name="id"]') || document.querySelector('[name="id"]');
      if (idInput && idInput.value) {
        var byId = this.variants.find(v => String(v.id) === String(idInput.value));
        if (byId) return byId;
      }
      return null;
    }

    checkExternalVariantChange() {
      var variant = this.getSelectedVariantFromDOM();
      if (!variant || variant.id === this._lastAppliedVariantId) return;
      this._lastAppliedVariantId = variant.id;
      this.applyVariant(variant);
    }

    // Apply a variant chosen outside the native picker: sync selected options, UI,
    // availability and everything updateVariant handles (incl. media-change).
    applyVariant(variant) {
      this.options.forEach((option, index) => {
        this.selectedOptions[option.name] = variant['option' + (index + 1)];
      });
      this.syncUIState();
      this.updateOptionAvailability();
      this.updateVariant(variant);
    }

    handleOptionClick(e) {
      const btn = e.currentTarget;
      if (btn.disabled) return;
      
      const optionName = btn.dataset.optionName;
      const optionValue = btn.dataset.optionValue;
      
      // Update active state
      const container = btn.closest('.product-variants-block__pills, .product-variants-block__swatches');
      if (container) {
        container.querySelectorAll('button').forEach(b => b.classList.remove('active'));
      }
      btn.classList.add('active');
      
      // Update label
      const label = btn.closest('.product-variants-block__option')?.querySelector('.product-variants-block__selected-value');
      if (label) label.textContent = ' ' + optionValue;
      
      // Update selection and find variant
      this.selectedOptions[optionName] = optionValue;
      this.updateOptionAvailability();
      
      const variant = this.findVariant();
      if (variant) {
        this.updateVariant(variant);
      }
    }
    
    handleDropdownChange(e, dropdown) {
      const optionName = dropdown.dataset.optionName;
      const optionValue = e.detail.value;
      
      const label = dropdown.closest('.product-variants-block__option')?.querySelector('.product-variants-block__selected-value');
      if (label) label.textContent = ' ' + optionValue;
      
      this.selectedOptions[optionName] = optionValue;
      this.updateOptionAvailability();
      
      const variant = this.findVariant();
      if (variant) {
        this.updateVariant(variant);
      }
    }
    
    findVariant() {
      return this.variants.find(variant => {
        return this.options.every((option, index) => {
          const optionKey = 'option' + (index + 1);
          return variant[optionKey] === this.selectedOptions[option.name];
        });
      });
    }
    
    updateOptionAvailability() {
      this.options.forEach((option, optionIndex) => {
        const optionKey = 'option' + (optionIndex + 1);
        
        option.values.forEach(value => {
          // Check if ANY variant with this option value is available
          // considering the OTHER currently selected options
          const isAvailable = this.variants.some(variant => {
            if (variant[optionKey] !== value) return false;
            if (!variant.available) return false;
            
            // Check if matches all OTHER selected options
            return this.options.every((otherOption, otherIndex) => {
              if (otherIndex === optionIndex) return true;
              const otherKey = 'option' + (otherIndex + 1);
              return variant[otherKey] === this.selectedOptions[otherOption.name];
            });
          });
          
          // Find and update button
          const btn = this.container.querySelector(
            `.product-variants-block__pill[data-option-name="${option.name}"][data-option-value="${value}"],
             .product-variants-block__swatch[data-option-name="${option.name}"][data-option-value="${value}"]`
          );
          
          if (btn) {
            btn.disabled = !isAvailable;
          }
        });
      });
    }
    
    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      return this.moneyFormat
        .replace(/\{\{\s*amount\s*\}\}/, amount)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100))
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','));
    }
    
    updateVariant(variant) {
      const isAvailable = variant.available === true;
      
      // Update hidden variant inputs
      this.container.querySelectorAll('input[name="id"], [data-variant-id]').forEach(input => {
        input.value = variant.id;
      });
      
      // Update price displays
      this.container.querySelectorAll('[id^="product-price-current"]').forEach(el => {
        el.textContent = this.formatMoney(variant.price);
        el.classList.toggle('product-price-block__current--on-sale', 
          variant.compare_at_price && variant.compare_at_price > variant.price);
      });
      
      this.container.querySelectorAll('[id^="product-price-compare"]').forEach(el => {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          el.textContent = this.formatMoney(variant.compare_at_price);
          el.style.display = '';
        } else {
          el.style.display = 'none';
        }
      });
      
      // Update add to cart buttons
      this.container.querySelectorAll('.product-add-to-cart-block__btn').forEach(btn => {
        const btnText = btn.querySelector('[data-add-to-cart-text]');
        btn.disabled = !isAvailable;
        if (btnText) btnText.textContent = isAvailable ? 'Add to Cart' : 'Sold Out';
      });
      
      // Update buy now buttons
      this.container.querySelectorAll('.shopify-payment-button button').forEach(btn => {
        btn.disabled = !isAvailable;
      });
      this.container.querySelectorAll('.product-add-to-cart-block__dynamic-checkout').forEach(container => {
        container.style.display = isAvailable ? '' : 'none';
      });
      
      // Update URL
      if (!this.sectionId) {
        const url = new URL(window.location);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
      
      // Update media gallery
      if (variant.featured_media) {
        document.dispatchEvent(new CustomEvent('product:media-change', {
          detail: { mediaId: variant.featured_media.id.toString(), sectionId: this.sectionId }
        }));
      }
      
      // Update stock block
      this.updateStockBlock(variant, isAvailable);
      
      // Dispatch event for other blocks
      window.dispatchEvent(new CustomEvent('variant:changed', {
        detail: { variant: variant }
      }));
    }
    
    updateStockBlock(variant, isAvailable) {
      this.container.querySelectorAll('.product-stock-block').forEach(stockBlock => {
        stockBlock.classList.remove('product-stock-block--in-stock', 'product-stock-block--low-stock', 'product-stock-block--out-of-stock');
        
        const message = stockBlock.querySelector('.product-stock-block__text');
        const barFill = stockBlock.querySelector('.product-stock-block__bar-fill');
        const threshold = parseInt(stockBlock.dataset.lowStockThreshold) || 5;
        const maxFillPercent = 80;
        let progressPercent = 0;
        
        if (!isAvailable) {
          stockBlock.classList.add('product-stock-block--out-of-stock');
          if (message) message.textContent = 'Out of Stock';
          progressPercent = 0;
        } else if (variant.inventory_management !== 'shopify') {
          stockBlock.classList.add('product-stock-block--in-stock');
          if (message) message.textContent = 'In Stock';
          progressPercent = maxFillPercent;
        } else if (variant.inventory_quantity <= 0) {
          stockBlock.classList.add('product-stock-block--in-stock');
          if (message) message.textContent = 'In Stock';
          progressPercent = maxFillPercent;
        } else if (variant.inventory_quantity <= threshold) {
          stockBlock.classList.add('product-stock-block--low-stock');
          if (message) message.textContent = `Only ${variant.inventory_quantity} left!`;
          progressPercent = Math.min((variant.inventory_quantity / threshold) * maxFillPercent, maxFillPercent);
        } else {
          stockBlock.classList.add('product-stock-block--in-stock');
          if (message) message.textContent = 'In Stock';
          progressPercent = maxFillPercent;
        }
        
        if (barFill) {
          barFill.style.setProperty('--progress', progressPercent + '%');
        }
      });
    }
  }

  /**
   * Product Media Gallery Class
   * Handles thumbnail navigation, swipe gestures, and media switching
   */
  class ProductMediaGallery {
    constructor(container) {
      this.container = container;
      this.id = container.id;
      this.layout = container.dataset.layout || 'collage';
      this.mobileLayout = container.dataset.mobileLayout || 'thumbnail';
      this.enableLightbox = container.dataset.enableLightbox === 'true';
      
      this.thumbnailsScroll = container.querySelector('.product-media-gallery__thumbnails-scroll');
      this.desktopFeatured = container.querySelector('.product-media-gallery__featured');
      this.thumbnails = container.querySelectorAll('.product-media-gallery__thumbnail');
      
      // Mobile carousel elements
      this.carouselSlider = container.querySelector('.product-media-gallery__carousel-slider');
      this.carouselItems = container.querySelectorAll('.product-media-gallery__carousel-item');
      
      this.currentIndex = 0;
      this.allMedia = [];
      
      // Build media data from thumbnails
      this.buildMediaData();
      
      this.init();
    }
    
    buildMediaData() {
      // Extract media info from each thumbnail
      this.thumbnails.forEach((thumb, index) => {
        const img = thumb.querySelector('.product-media-gallery__thumbnail-image');
        if (img) {
          // Get high-res src from thumbnail src by replacing width
          const thumbSrc = img.src;
          const highResSrc = thumbSrc.replace(/width=\d+/, 'width=1200');
          const lightboxSrc = thumbSrc.replace(/width=\d+/, 'width=2400');
          
          this.allMedia.push({
            index: index,
            id: thumb.dataset.mediaId,
            type: 'image', // Could be extended for video/model
            src: highResSrc,
            srcset: `${thumbSrc.replace(/width=\d+/, 'width=600')} 600w, ${thumbSrc.replace(/width=\d+/, 'width=900')} 900w, ${highResSrc} 1200w`,
            lightboxSrc: lightboxSrc,
            alt: img.alt || ''
          });
        }
      });
    }
    
    init() {
      this.bindThumbnailClicks();
      this.bindSwipeGestures();
      // Variant -> media syncing is handled centrally in product-media-gallery.js
      // (the carousel owner) so the autoplay carousel and the desktop main image
      // stay in sync across all layouts. See initVariantMediaSync there.

      if (this.enableLightbox) {
        this.bindLightbox();
      }
    }
    
    bindThumbnailClicks() {
      if (!this.thumbnailsScroll) return;
      
      this.thumbnailsScroll.addEventListener('click', (e) => {
        const thumb = e.target.closest('.product-media-gallery__thumbnail');
        if (!thumb) return;
        
        const index = parseInt(thumb.dataset.index);
        this.goToSlide(index);
      });
    }
    
    bindSwipeGestures() {
      if (!this.carouselSlider) return;
      
      let touchStartX = 0;
      let touchStartY = 0;
      let touchEndX = 0;
      let touchEndY = 0;
      const minSwipeDistance = 50;
      
      this.carouselSlider.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].screenX;
        touchStartY = e.changedTouches[0].screenY;
      }, { passive: true });
      
      this.carouselSlider.addEventListener('touchend', (e) => {
        touchEndX = e.changedTouches[0].screenX;
        touchEndY = e.changedTouches[0].screenY;
        
        const deltaX = touchEndX - touchStartX;
        const deltaY = touchEndY - touchStartY;
        
        // Only handle horizontal swipes
        if (Math.abs(deltaX) < minSwipeDistance || Math.abs(deltaY) > Math.abs(deltaX)) {
          return;
        }
        
        if (deltaX < 0) {
          // Swipe left - next
          this.next();
        } else {
          // Swipe right - previous
          this.prev();
        }
      }, { passive: true });
    }
    
    bindVariantMediaChange() {
      document.addEventListener('product:media-change', (e) => {
        const mediaId = e.detail.mediaId;
        const thumb = this.container.querySelector(`[data-media-id="${mediaId}"]`);
        
        if (thumb && thumb.dataset.index) {
          this.goToSlide(parseInt(thumb.dataset.index));
        }
      });
    }
    
    bindLightbox() {
      this.container.querySelectorAll('.product-media-gallery__image').forEach(img => {
        img.style.cursor = 'zoom-in';
        img.addEventListener('click', () => {
          const lightboxSrc = img.dataset.lightboxSrc || img.src;
          this.openLightbox(lightboxSrc, img.alt);
        });
      });
    }
    
    goToSlide(index) {
      const maxIndex = Math.max(this.thumbnails.length, this.carouselItems.length);
      if (index < 0 || index >= maxIndex) return;
      
      this.currentIndex = index;
      
      // Update active thumbnail
      this.thumbnails.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === index);
      });
      
      // Scroll thumbnail into view
      const activeThumb = this.thumbnails[index];
      if (activeThumb) {
        activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
      
      // Update mobile carousel
      this.updateMobileCarousel(index);
      
      // Update desktop featured image
      this.updateDesktopFeaturedImage(index);
    }
    
    updateMobileCarousel(index) {
      if (!this.carouselItems.length) return;
      
      // Update active state on carousel items (CSS handles opacity/visibility)
      this.carouselItems.forEach((item, i) => {
        if (i === index) {
          item.classList.add('is-active');
          item.classList.remove('is-leaving');
        } else if (item.classList.contains('is-active')) {
          // Add leaving class for smooth transition out
          item.classList.add('is-leaving');
          item.classList.remove('is-active');
          // Remove leaving class after transition
          setTimeout(() => item.classList.remove('is-leaving'), 500);
        } else {
          item.classList.remove('is-active', 'is-leaving');
        }
      });
    }
    
    updateDesktopFeaturedImage(index) {
      if (!this.desktopFeatured || !this.allMedia[index]) return;
      
      const media = this.allMedia[index];
      const image = this.desktopFeatured.querySelector('.product-media-gallery__featured-image');
      
      if (image && media.type === 'image') {
        // Animate transition with GSAP if available
        if (typeof gsap !== 'undefined') {
          gsap.to(image, {
            opacity: 0,
            scale: 0.98,
            duration: 0.15,
            ease: 'power2.in',
            onComplete: () => {
              // Update image source
              image.src = media.src;
              image.srcset = media.srcset;
              image.alt = media.alt;
              image.dataset.lightboxSrc = media.lightboxSrc;
              
              // Update container data attributes
              this.desktopFeatured.dataset.mediaId = media.id;
              this.desktopFeatured.dataset.index = index;
              
              // Animate in
              gsap.to(image, {
                opacity: 1,
                scale: 1,
                duration: 0.2,
                ease: 'power2.out'
              });
            }
          });
        } else {
          // Fallback without animation
          image.src = media.src;
          image.srcset = media.srcset;
          image.alt = media.alt;
          image.dataset.lightboxSrc = media.lightboxSrc;
          this.desktopFeatured.dataset.mediaId = media.id;
          this.desktopFeatured.dataset.index = index;
        }
      }
    }
    
    // Index of the currently visible slide, read from the DOM so it stays correct
    // even after a variant/colour change was driven by another controller.
    getActiveSlideIndex() {
      const items = this.carouselItems.length ? this.carouselItems : this.thumbnails;
      for (let i = 0; i < items.length; i++) {
        if (items[i].classList.contains('is-active')) return i;
      }
      return this.currentIndex;
    }

    isSlideHidden(i) {
      const item = this.carouselItems[i] || this.thumbnails[i];
      return item ? item.classList.contains('pmg-hidden') : true;
    }

    next() {
      const total = Math.max(this.thumbnails.length, this.carouselItems.length);
      if (!total) return;
      let idx = this.getActiveSlideIndex();
      // Advance to the next slide that is visible (colour filtering may hide some).
      for (let step = 1; step <= total; step++) {
        const candidate = (idx + step) % total;
        if (!this.isSlideHidden(candidate)) { this.goToSlide(candidate); return; }
      }
    }

    prev() {
      const total = Math.max(this.thumbnails.length, this.carouselItems.length);
      if (!total) return;
      let idx = this.getActiveSlideIndex();
      for (let step = 1; step <= total; step++) {
        const candidate = (idx - step + total) % total;
        if (!this.isSlideHidden(candidate)) { this.goToSlide(candidate); return; }
      }
    }
    
    openLightbox(src, alt) {
      // Create lightbox overlay
      const overlay = document.createElement('div');
      overlay.className = 'product-lightbox';
      overlay.innerHTML = `
        <div class="product-lightbox__backdrop"></div>
        <div class="product-lightbox__content">
          <img src="${src}" alt="${alt || ''}" class="product-lightbox__image">
          <button type="button" class="product-lightbox__close" aria-label="Close">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
          </button>
        </div>
      `;
      
      document.body.appendChild(overlay);
      document.body.style.overflow = 'hidden';
      
      // Animate in with GSAP if available
      if (typeof gsap !== 'undefined') {
        gsap.fromTo(overlay, { opacity: 0 }, { opacity: 1, duration: 0.3 });
        gsap.fromTo(overlay.querySelector('.product-lightbox__image'), 
          { scale: 0.9, opacity: 0 }, 
          { scale: 1, opacity: 1, duration: 0.3, delay: 0.1 }
        );
      }
      
      // Close handlers
      const close = () => {
        if (typeof gsap !== 'undefined') {
          gsap.to(overlay, { 
            opacity: 0, 
            duration: 0.2, 
            onComplete: () => {
              overlay.remove();
              document.body.style.overflow = '';
            }
          });
        } else {
          overlay.remove();
          document.body.style.overflow = '';
        }
      };
      
      overlay.querySelector('.product-lightbox__close').addEventListener('click', close);
      overlay.querySelector('.product-lightbox__backdrop').addEventListener('click', close);
      overlay.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') close();
      });
    }
  }

  /**
   * Initialize galleries on DOM ready
   */
  function init() {
    document.querySelectorAll('.product-media-gallery').forEach(gallery => {
      new ProductMediaGallery(gallery);
    });
  }
  
  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
  // Re-initialize on Shopify section refresh
  if (window.Shopify?.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      const gallery = event.target.querySelector('.product-media-gallery');
      if (gallery) {
        new ProductMediaGallery(gallery);
      }
    });
  }
  
  // Expose for external use
  window.ProductMediaGallery = ProductMediaGallery;
  window.ProductVariantSelector = ProductVariantSelector;
})();

/**
 * Add lightbox styles dynamically
 */
(function() {
  const style = document.createElement('style');
  style.textContent = `
    .product-lightbox {
      position: fixed;
      inset: 0;
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .product-lightbox__backdrop {
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.9);
    }
    
    .product-lightbox__content {
      position: relative;
      max-width: 90vw;
      max-height: 90vh;
    }
    
    .product-lightbox__image {
      max-width: 100%;
      max-height: 90vh;
      object-fit: contain;
    }
    
    .product-lightbox__close {
      position: absolute;
      top: -40px;
      right: 0;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: transparent;
      border: none;
      color: white;
      cursor: pointer;
      transition: opacity 0.2s;
    }
    
    .product-lightbox__close:hover {
      opacity: 0.7;
    }
  `;
  document.head.appendChild(style);
})();
